import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { processCatalogTool } from '../../../api/catalog_tool';
import './DomainRemovalCard.scss';

const POST_ACTION_LABELS = {
  requeue: 're-queue the URLs for matching',
  dismiss: 'dismiss the URLs from queues',
  archive: 'archive the domains from all queues'
};

/**
 * Brand-only "Remove entire domain" control: unlinks every matched URL on the chosen brand
 * domains. Deliberately one level deeper than the happy path — expand, pick domains, then a
 * dry-run count confirms the blast radius before anything runs.
 */
const DomainRemovalCard = props => {
  const { brand, postAction, onReport } = props;

  const [isExpanded, setIsExpanded] = useState(false);
  const [chosenDomains, setChosenDomains] = useState([]);
  const [isChecking, setIsChecking] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  const toggleDomain = domain => {
    setChosenDomains(current => (current.includes(domain) ? current.filter(d => d !== domain) : [...current, domain]));
  };

  const collapse = () => {
    setIsExpanded(false);
    setChosenDomains([]);
  };

  const runRemoval = async () => {
    setIsRunning(true);
    try {
      const response = await processCatalogTool({ mode: 'unlink_domain', AllBrand_id: brand.id, domains: chosenDomains, postAction, dryRun: false });
      onReport(response.data);
      window.ALERT.success(`Removed ${response.data.processed} URLs across ${chosenDomains.length} ${chosenDomains.length === 1 ? 'domain' : 'domains'}`);
      collapse();
    } catch (error) {
      window.ALERT.error(error || 'Failed to remove domains');
    } finally {
      setIsRunning(false);
    }
  };

  const handleRemove = async () => {
    setIsChecking(true);
    try {
      const response = await processCatalogTool({ mode: 'unlink_domain', AllBrand_id: brand.id, domains: chosenDomains, dryRun: true });
      const { urlCount, pinCount, variantCount, domains, conflictsSkipped } = response.data;
      if (!urlCount) return window.ALERT.info('No matched URLs on the selected domains');
      confirmAlert({
        title: 'Remove entire domains?',
        message: `This will unlink ${urlCount.toLocaleString()} URLs, ~${pinCount.toLocaleString()} pins and ${variantCount.toLocaleString()} variants across ${domains.join(
          ', '
        )} for ${brand.name}, then ${POST_ACTION_LABELS[postAction]} (selected under "After unlinking").${
          conflictsSkipped ? ` ${conflictsSkipped.toLocaleString()} URLs tied to other products will be skipped.` : ''
        }`,
        buttons: [
          { label: 'Cancel', onClick: () => {} },
          { label: 'Remove', onClick: runRemoval }
        ]
      });
    } catch (error) {
      window.ALERT.error(error || 'Failed to preview domain removal');
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className='catalog-domain-removal'>
      {!isExpanded ? (
        <button type='button' className='expand-toggle' onClick={() => setIsExpanded(true)}>
          <span className='toggle-title'>Remove entire domain…</span>
          <span className='toggle-description'>Only available when brand-scoped</span>
        </button>
      ) : (
        <div className='removal-body'>
          <div className='removal-heading'>Remove entire domain</div>
          <div className='removal-description'>
            Unlinks every matched URL on the chosen domains of {brand.name} — not just pasted ones.
          </div>
          <div className='domain-checkboxes'>
            {brand.domains.map(({ domain, isPrimary }) => (
              <label key={domain} className='domain-checkbox'>
                <input type='checkbox' checked={chosenDomains.includes(domain)} onChange={() => toggleDomain(domain)} />
                <span className='checkbox-domain'>{domain}</span>
                {isPrimary && <span className='checkbox-primary'>primary</span>}
              </label>
            ))}
          </div>
          <div className='removal-actions'>
            <button type='button' className='removal-run' disabled={!chosenDomains.length || isChecking || isRunning} onClick={handleRemove}>
              {isRunning ? 'Removing…' : isChecking ? 'Checking…' : `Remove ${chosenDomains.length || ''} ${chosenDomains.length === 1 ? 'domain' : 'domains'}`}
            </button>
            <button type='button' className='removal-cancel' onClick={collapse}>
              cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

DomainRemovalCard.propTypes = {
  brand: PropTypes.object.isRequired,
  postAction: PropTypes.oneOf(['requeue', 'dismiss', 'archive']).isRequired,
  onReport: PropTypes.func.isRequired
};

export default DomainRemovalCard;
